import { useBlockPageClicks, useElementRect } from './hooks';
import { useTourKeyboardNavigation } from './tourKeyboardNavigation';
import { AnchoredTourCard, CenteredTourCard } from './TourCard';
import { TourSpotlight, type TourSpotlightProps } from './TourSpotlight';
import { TopLayerPortal } from './topLayerPortal';
import { toStepKey, useStepResolution } from './stepResolution';
import { toBoundingRect } from './spotlightGeometry';
import { useTourTracking } from './useTourTracking';
import type { TrackTourEvent } from './tourTracking';
import type { ActiveTour, Rect, TourStep } from './types';
import type { I18nLocale } from '@ardoq/i18n';

export type TourViewProps = {
  activeTour: ActiveTour;
  i18nLocale: I18nLocale;
  trackTourEvent: TrackTourEvent;
  onNext: VoidFunction;
  onPrev: VoidFunction;
  onClose: VoidFunction;
};

type Highlight = { element: HTMLElement; rect: Rect };

type SpotlightArgs = {
  anchor: Highlight | null;
  secondary: Highlight | null;
  isCentered: boolean;
  allowInteraction: boolean;
  trackMovingAnchor: boolean;
};

const toSpotlightProps = ({
  anchor,
  secondary,
  isCentered,
  allowInteraction,
  trackMovingAnchor,
}: SpotlightArgs): TourSpotlightProps => {
  if (!anchor) {
    return {
      primary: null,
      secondary: null,
      dimAll: isCentered,
      allowInteraction,
      trackMovingAnchor: false,
    };
  }
  return {
    primary: anchor,
    secondary,
    dimAll: false,
    allowInteraction,
    trackMovingAnchor,
  };
};

type HighlightsArgs = {
  anchor: Highlight | null;
  secondary: Highlight | null;
  isHighlightMerged: boolean;
};

export const toHighlights = ({
  anchor,
  secondary,
  isHighlightMerged,
}: HighlightsArgs): {
  anchor: Highlight | null;
  secondary: Highlight | null;
} => {
  if (!isHighlightMerged || !anchor || !secondary) {
    return { anchor, secondary };
  }
  return {
    anchor: {
      element: anchor.element,
      rect: toBoundingRect(anchor.rect, secondary.rect),
    },
    secondary: null,
  };
};

const ownsFocus = (step: TourStep | null): boolean =>
  step !== null && step.allowInteraction !== 'all' && !step.pageKeepsFocus;

export const TourView = ({
  activeTour,
  i18nLocale,
  trackTourEvent,
  onNext,
  onPrev,
  onClose,
}: TourViewProps) => {
  const { definition, stepIndex } = activeTour;
  const step = definition.steps[stepIndex] ?? null;

  const resolution = useStepResolution(activeTour);
  const { completeRun, dismissRun, feedbackSentiment, giveFeedback } =
    useTourTracking({
      activeTour,
      resolution,
      trackTourEvent,
      onClose,
    });

  const anchorTargetEl =
    resolution?.status === 'anchored' ? resolution.element : null;
  const secondaryTargetEl =
    resolution?.status === 'anchored' ? resolution.secondary : null;

  const shouldTrackMovingAnchor = Boolean(step?.trackMovingAnchor);
  useBlockPageClicks(step?.allowInteraction === 'hover');
  const rect = useElementRect(anchorTargetEl, shouldTrackMovingAnchor);
  const secondaryRect = useElementRect(
    secondaryTargetEl,
    shouldTrackMovingAnchor
  );

  const isCentered =
    step !== null &&
    (step.target === null ||
      resolution?.status === 'fallback' ||
      resolution?.status === 'resolving');
  const { anchor, secondary } = toHighlights({
    anchor: anchorTargetEl && rect ? { element: anchorTargetEl, rect } : null,
    secondary:
      secondaryTargetEl && secondaryRect
        ? { element: secondaryTargetEl, rect: secondaryRect }
        : null,
    isHighlightMerged: Boolean(step?.isHighlightMerged),
  });
  const isCardShowing = isCentered || anchor !== null;

  const tourOwnsFocus = ownsFocus(step);

  useTourKeyboardNavigation({
    isStepShowing: isCardShowing,
    stepIndex,
    totalSteps: definition.steps.length,
    isForwardStepOnly: Boolean(step?.isForwardStepOnly),
    onNext,
    onPrev,
    onComplete: completeRun,
    onDismiss: dismissRun,
  });

  if (!step) return null;

  // Child effects flush before the parent's, so a key that changed only with the
  // step would re-show the host before `beforeShow` had even run. Keying on the
  // resolution's status too re-shows it as the step moves on, which is what
  // puts a re-show after a modal `beforeShow` opened enters the top layer.
  const stepKey = toStepKey({
    tourId: definition.id,
    stepIndex,
    stepId: step.id,
  });
  const reassertKey = `${stepKey}:${resolution?.status ?? 'pending'}`;

  const shouldAllowInteraction =
    step.allowInteraction === 'all' || step.allowInteraction === 'hover';

  const cardContent = {
    title: step.title,
    dialogLabel: definition.dialogLabel,
    body: step.body,
    stepIndex,
    totalSteps: definition.steps.length,
    isForwardStepOnly: step.isForwardStepOnly,
    shouldTakeFocus: tourOwnsFocus,
    onBack: onPrev,
    onNext,
    onComplete: completeRun,
    onDismiss: dismissRun,
    feedbackSentiment,
    onFeedback: giveFeedback,
    i18nLocale,
  };

  return (
    <TopLayerPortal reassertKey={reassertKey}>
      <TourSpotlight
        {...toSpotlightProps({
          anchor: isCentered ? null : anchor,
          secondary,
          isCentered,
          allowInteraction: shouldAllowInteraction,
          trackMovingAnchor: shouldTrackMovingAnchor,
        })}
      />
      {isCentered && (
        <CenteredTourCard
          {...cardContent}
          isIntroModal={stepIndex === 0 && step.target === null}
          isResolving={resolution?.status === 'resolving'}
          shouldTakeFocus={tourOwnsFocus && resolution !== null}
        />
      )}
      {!isCentered && anchor && (
        <AnchoredTourCard
          rect={anchor.rect}
          placement={step.placement ?? 'bottom'}
          {...cardContent}
        />
      )}
    </TopLayerPortal>
  );
};
